import { trackGoogleSignupCompleted } from "@/lib/conversionAnalytics";
import { CLOCK_SKEW_ERROR } from '@/lib/authClockSkew';
import { identifyUser, resetAnalytics, getDeviceId } from '@/lib/analytics';
import { trackLoginOnce } from '@/lib/productActivity';
import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { captureException, setSentryUser, clearSentryUser, addSentryBreadcrumb } from '@/lib/sentry';
import { captureReferralFromEnv, getCapturedReferral, clearCapturedReferral } from '@/lib/affiliateReferral';
import { captureMetaAttributionFromEnv, getMetaAttribution, clearMetaAttribution } from '@/lib/metaAttribution';
import { saveGoogleSignupRedirect } from '@/lib/googleSignup';
import { clearSignedOutMarker, markSignedOut } from '@/lib/auth/writeSession';

export type AppRole = 'admin' | 'user' | 'crew' | 'super_admin' | 'marketing_agency' | 'affiliate' | 'support_staff';

// Boot-time sanitation (2026-07-22 session-poisoning incident): if the portal
// storage key holds a session for the SAME user as the main session, it can
// only be a captured copy of a contractor login (portal users are customer
// accounts) — and the portal client refreshing it would burn the main
// session's refresh-token family, signing the contractor out. Drop it at
// module eval, before the (lazy-loaded) portal client could ever refresh it.
(() => {
  try {
    const readUserId = (raw: string | null) => {
      try { return JSON.parse(raw ?? '')?.user?.id as string | undefined; }
      catch { return undefined; }
    };
    const portalUserId = readUserId(localStorage.getItem('lightmaster-client-portal-auth'));
    if (!portalUserId) return;
    const mainKey = Object.keys(localStorage).find((k) => /^sb-.+-auth-token$/.test(k));
    if (mainKey && readUserId(localStorage.getItem(mainKey)) === portalUserId) {
      localStorage.removeItem('lightmaster-client-portal-auth');
    }
  } catch { /* storage unavailable (tests) — nothing to sanitize */ }
})();

// set_contact_attribution isn't in the generated Supabase types yet; cast the
// client to call it in a type-safe shape (mirrors the proposal-public RPC casts).
type SetContactAttributionRpc = {
  rpc(
    fn: 'set_contact_attribution',
    args: {
      p_fbclid: string | null;
      p_fbc: string | null;
      p_utm_source: string | null;
      p_utm_medium: string | null;
      p_utm_campaign: string | null;
      p_utm_term: string | null;
      p_utm_content: string | null;
    },
  ): Promise<{ error: Error | null }>;
};

// Deferred CompleteRegistration: with email confirmation required, signUp()
// has no session, so the CAPI event fires on the user's first authenticated
// arrival instead. Attribution rides in user_metadata (captured at signUp), so
// this works cross-device; meta_reg_tracked makes it once-per-user; the 7-day
// window keeps stale accounts from emitting registrations.
async function fireDeferredRegistrationTracking(user: User): Promise<void> {
  try {
    const md = (user.user_metadata ?? {}) as Record<string, unknown>;
    if (md.meta_reg_tracked) return;
    const created = user.created_at ? Date.parse(user.created_at) : NaN;
    if (!Number.isFinite(created) || Date.now() - created > 7 * 24 * 3600 * 1000) return;

    // Google OAuth users are intentionally authenticated before their profile
    // is provisioned. Registration is complete only after required data capture.
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', user.id)
      .maybeSingle();
    if (profileError || !profile) return;

    const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
    const { error } = await supabase.functions.invoke('track-conversion', {
      body: {
        event: 'CompleteRegistration',
        event_id: `reg_${user.id}`,
        fbc: str(md.meta_fbc),
        fbp: str(md.meta_fbp),
        utm: {
          utm_source: str(md.utm_source),
          utm_medium: str(md.utm_medium),
          utm_campaign: str(md.utm_campaign),
          utm_term: str(md.utm_term),
          utm_content: str(md.utm_content),
        },
      },
    });
    if (error) {
      // Leave the flag unset so the next sign-in retries.
      console.error('deferred track-conversion (CompleteRegistration) failed', error);
      return;
    }
    // Contact attribution is stamped server-side by the signup trigger
    // (sync_profile_to_marketing_contact reads the same metadata) — no RPC
    // needed here. Mark done so later sign-ins don't re-emit.
    await supabase.auth.updateUser({ data: { meta_reg_tracked: true } });
  } catch (e) {
    console.error('deferred registration tracking threw', e);
  }
}

export type ProfileStatus =
  | 'loading'
  | 'ready'
  /**
   * Signed in, verified, not invited, and no profile yet: a new Google identity
   * that still owes us a phone number, or an email signup held for a team
   * join request (its business domain already belongs to an organization).
   * /auth/complete-profile finishes either.
   */
  | 'signup_completion_required'
  | 'missing_profile_error'
  /**
   * An admin deactivated this team member (#1390). Their login is banned and
   * their sessions revoked; a still-valid token lands here and sees the
   * AccountDeactivated screen instead of a broken app.
   */
  | 'deactivated'
  /**
   * Accepted a team invitation while every seat on the team's plan was in use
   * (#1399). The profile stays inactive and hidden until a seat frees up; the
   * AwaitingSeat screen lets them ask again (claim_my_team_seat).
   */
  | 'awaiting_seat';

const hasInvitationMetadata = (user: User): boolean =>
  Boolean(user.invited_at)
  || Object.prototype.hasOwnProperty.call(user.user_metadata ?? {}, 'organization_id')
  || Object.prototype.hasOwnProperty.call(user.user_metadata ?? {}, 'role');

type CompleteGoogleSignupRpc = {
  rpc(
    fn: 'complete_google_signup',
    args: {
      p_phone: string;
      p_business_name: string | null;
      p_referral_code: string | null;
      p_meta_fbc: string | null;
      p_meta_fbp: string | null;
      p_fbclid: string | null;
      p_utm_source: string | null;
      p_utm_medium: string | null;
      p_utm_campaign: string | null;
      p_utm_term: string | null;
      p_utm_content: string | null;
      p_separate_business: boolean;
    },
  ): Promise<{ data: string | null; error: Error | null }>;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  signUp: (email: string, password: string, fullName: string, phoneNumber: string, businessName?: string) => Promise<{ error: unknown; needsConfirmation?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: unknown }>;
  signInWithGoogle: (redirectTo?: string | null) => Promise<{ error: unknown }>;
  /**
   * `held` means the server opened a team join request instead of founding
   * an organization (the email's business domain already belongs to one).
   */
  completeGoogleSignup: (
    phoneNumber: string,
    businessName?: string,
    options?: { separateBusiness?: boolean },
  ) => Promise<{ error: unknown; held?: boolean }>;
  /** Re-run the profile lookup (a held signup was just approved, for instance). */
  refreshProfile: () => void;
  signOut: () => Promise<{ error: unknown }>;
  loading: boolean;
  profileStatus: ProfileStatus;
  /** Resolved account/org identity; null until its profile has been verified. */
  accessScope: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Supabase reports a mistyped password the same way it reports an outage: an
 * error back from the auth call. Capturing all of them buried real auth failures
 * under people fat-fingering their login — LIGHTMASTER-WEB-W was 28 events, every
 * one `Invalid login credentials`, and LIGHTMASTER-WEB-10 the sign-up equivalent.
 * With the stream full of those, a genuine regression looks like more of the same.
 *
 * Expected rejections become breadcrumbs (still visible on any exception that
 * follows). Anything unrecognised is still captured — the allowlist is
 * deliberately narrow so a new failure mode surfaces rather than hides.
 */
const EXPECTED_AUTH_ERRORS = [
  'invalid login credentials',
  'email not confirmed',
  'user already registered',
  'a user with this email address has already been registered',
  'email rate limit exceeded',
  'for security purposes',        // Supabase's resend/rate-limit copy
  'password should be at least',
  // Supabase's HIBP check, added when leaked-password protection is on. This is
  // a person picking "summer2026", not a fault — but it was not on the list, so
  // LIGHTMASTER-WEB-10 kept firing after the rest of sign-up was quietened
  // (still reporting on 2026-08-16, release af75c4a).
  'password is known to be weak',
  // Sibling messages from the same password policy, allowlisted now rather than
  // one Sentry issue at a time.
  'password should contain at least',
  'signups not allowed',
  'invalid email',
  // A deactivated team member's banned login (#1390). Expected, not a fault.
  'user is banned',
];

export const ACCOUNT_DEACTIVATED_TITLE = 'Your account has been deactivated';
export const ACCOUNT_DEACTIVATED_DESCRIPTION = 'Contact your admin to restore access.';
export const AWAITING_SEAT_TITLE = "Your team's plan is full";
export const AWAITING_SEAT_DESCRIPTION =
  "You accepted your invitation, but every seat on your team's plan is in use. Ask your admin to free a seat, then choose Check again.";

/** GoTrue refuses a banned login (a deactivated team member) with user_banned. */
export function isAccountDeactivatedAuthError(
  error: { message?: string; code?: string } | null | undefined,
): boolean {
  if (!error) return false;
  return error.code === 'user_banned' || /user is banned/i.test(error.message ?? '');
}

/** How often a signed-in tab re-asks whether its account was deactivated. */
export const ACCOUNT_STATUS_POLL_MS = 2 * 60 * 1000;
const ACCOUNT_STATUS_MIN_GAP_MS = 15 * 1000;

/**
 * Whether the signed-in user's account was deactivated by an admin (#1390).
 * A deactivated member cannot read any profiles row (their own included), so
 * this asks get_my_account_status. Null when the answer is unknown (error).
 */
export interface AccountStatusFlags {
  deactivated: boolean;
  /** Accepted an invitation at a full plan (#1399). */
  awaitingSeat: boolean;
}

/** Reads get_my_account_status' flags; null when the answer is unknown (error). */
export function accountStatusFlags(data: unknown): AccountStatusFlags | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as { deactivated?: unknown; awaiting_seat?: unknown };
  return { deactivated: d.deactivated === true, awaitingSeat: d.awaiting_seat === true };
}

export async function fetchAccountStatus(): Promise<AccountStatusFlags | null> {
  try {
    const { data, error } = await supabase.rpc('get_my_account_status');
    if (error) return null;
    return accountStatusFlags(data);
  } catch {
    return null;
  }
}

export async function fetchAccountDeactivated(): Promise<boolean | null> {
  const status = await fetchAccountStatus();
  return status === null ? null : status.deactivated;
}

export function isExpectedAuthError(error: { message?: string; code?: string; status?: number }): boolean {
  if (error?.code === CLOCK_SKEW_ERROR) return true;
  const msg = (error?.message ?? '').toLowerCase();
  if (EXPECTED_AUTH_ERRORS.some((e) => msg.includes(e))) return true;
  // 429 is always a rate limit, never a bug worth paging on.
  return error?.status === 429;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const currentUserRef = useRef(user);
  currentUserRef.current = user;
  const userId = user?.id;
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<AppRole | null>(null);
  const [profileStatus, setProfileStatus] = useState<ProfileStatus>('loading');
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);
  const [resolvedAccess, setResolvedAccess] = useState<{ userId: string; scope: string } | null>(null);

  useEffect(() => {
    // Capture an affiliate referral (?ref= or the .lightmaster.io cookie) once.
    captureReferralFromEnv();

    // Capture Meta ad attribution (?fbclid=/utm_*/_fbp cookie) once, alongside it.
    captureMetaAttributionFromEnv();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Add breadcrumb for auth events
        addSentryBreadcrumb(`Auth event: ${event}`, 'auth', {
          userId: session?.user?.id,
          hasSession: !!session
        });

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Clear Sentry user context on sign out
        if (event === 'SIGNED_OUT') {
          clearSentryUser();
        }
        // A real sign-in lifts the "signed out on this browser" marker that
        // stops other tabs from silently restoring a session (#1657).
        if (event === 'SIGNED_IN' && session?.user) {
          clearSignedOutMarker();
        }

        // Deferred registration tracking: with email confirmation required,
        // signUp() returns no session, so the CompleteRegistration CAPI event
        // can only fire on the user's FIRST authenticated arrival (the
        // confirm-link redirect, or first login). Attribution rides in
        // user_metadata, so this works even on a different device. The
        // meta_reg_tracked flag (written to user_metadata) makes it
        // once-per-user; the 7-day window keeps stale accounts out of Meta.
        if (event === 'SIGNED_IN' && session?.user) {
          // Defer past the auth callback (Supabase deadlocks on client calls
          // made synchronously inside onAuthStateChange).
          setTimeout(() => void fireDeferredRegistrationTracking(session.user), 0);
          setTimeout(() => trackLoginOnce(), 0);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // A stored session at load means this user is signed in here again.
      if (session?.user) clearSignedOutMarker();
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch profile role whenever the user (id) changes.
  useEffect(() => {
    const user = currentUserRef.current;
    setResolvedAccess(null);
    if (!user?.id) {
      setRole(null);
      setProfileStatus('ready');
      return;
    }

    // Clear any role carried over from a previous account BEFORE fetching the
    // new one. Without this, switching accounts (log out → log in as someone
    // else) leaves the prior role in state, and route guards act on it during
    // the transition — e.g. a super-admin briefly seen as 'admin' (or vice
    // versa) gets bounced to /forbidden. Nulling it makes guards show their
    // loading state until the correct role resolves.
    setRole(null);
    setProfileStatus('loading');

    let cancelled = false;
    // A network blip right at sign-in ("TypeError: Failed to fetch", Sentry
    // LIGHTMASTER-WEB-Z) used to fail closed immediately: an admin landed as
    // 'crew' until they reloaded. Retry transient transport errors a couple of
    // times before giving up; every other error still fails closed at once.
    const isTransient = (message: string | undefined) =>
      /failed to fetch|networkerror|load failed|network request failed/i.test(message ?? '');
    const fetchProfileRole = async () => {
      let last: Awaited<ReturnType<typeof queryRole>> | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const result = await queryRole();
        if (cancelled || !result.error || !isTransient(result.error.message)) return result;
        last = result;
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1) ** 2));
      }
      return last!;
    };
    const queryRole = () => supabase
      .from('profiles')
      .select('role, organization_id')
      .eq('id', user.id)
      .maybeSingle();

    const fetchRole = async () => {
      let accountStatus: AccountStatusFlags | null = null;
      try {
        const { data, error } = await fetchProfileRole();

        if (cancelled) return; // a newer account switch superseded this fetch

        if (error) {
          captureException(new Error('Failed to fetch user role'), {
            supabaseError: error,
            userId: user.id,
            userEmail: user.email
          });
          console.error('Error fetching user role:', error);
          // Fail CLOSED: a transient role-fetch error must NOT escalate the
          // user to admin. Drop to least-privilege; a reload re-attempts.
          setRole('crew');
          setProfileStatus('ready');
        } else if (data?.role) {
          setResolvedAccess({ userId: user.id, scope: `${user.id}:${data.organization_id ?? 'platform'}:${profileRefreshKey}` });
          setRole(data.role as AppRole);
          setProfileStatus('ready');
          identifyUser({
            userId: user.id,
            email: user.email,
            role: data.role as string,
            organizationId: (data as { organization_id?: string | null }).organization_id
              ?? ((user.user_metadata as Record<string, unknown> | undefined)?.organization_id as string | undefined)
              ?? null,
          });
          // Set user context in Sentry after successful role fetch
          setSentryUser({
            id: user.id,
            email: user.email,
            role: data.role as AppRole
          });
        } else if ((accountStatus = await fetchAccountStatus())?.deactivated) {
          // The row exists but RLS hides it: an admin deactivated them.
          if (cancelled) return;
          setRole(null);
          setProfileStatus('deactivated');
        } else if (accountStatus?.awaitingSeat) {
          // The row exists but RLS hides it: they accepted at a full plan.
          if (cancelled) return;
          setRole(null);
          setProfileStatus('awaiting_seat');
        } else {
          if (cancelled) return;
          // A verified, uninvited identity without a profile is the one
          // intentional missing-profile state: a new Google identity that must
          // finish required data capture, or a signup held for a team join
          // request. Invitation-shaped missing profiles remain anomalies.
          if (!hasInvitationMetadata(user)) {
            setProfileStatus('signup_completion_required');
          } else {
            console.warn('No profile found for authenticated user');
            setProfileStatus('missing_profile_error');
          }
        }
      } catch (error) {
        if (cancelled) return;
        captureException(error as Error, {
          context: 'fetchUserRole',
          userId: user.id
        });
        console.error('Failed to fetch role:', error);
        // Fail CLOSED (see above) — never escalate to admin on error.
        setRole('crew');
        setProfileStatus('ready');
      }
    };
    void fetchRole();
    return () => { cancelled = true; };
  // A session refresh supplies a new User object for the same identity. Do
  // not put ProtectedRoute back into its loading state: that unmounts every
  // open editor and destroys unsaved work. Real account changes and explicit
  // profile refreshes still revalidate and fail closed as before.
  }, [userId, profileRefreshKey]);

  // A member deactivated while their tab is open (#1390): their queries go
  // empty at once, and their token refresh is refused within the hour. Re-ask
  // on focus and every few minutes so they see the deactivated screen rather
  // than a blank app.
  useEffect(() => {
    if (!userId || profileStatus !== 'ready') return;
    let stopped = false;
    let lastCheck = 0;
    const check = async (force = false) => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (!force && now - lastCheck < ACCOUNT_STATUS_MIN_GAP_MS) return;
      lastCheck = now;
      const deactivated = await fetchAccountDeactivated();
      if (!stopped && deactivated) {
        setRole(null);
        setProfileStatus('deactivated');
      }
    };
    const onFocus = () => { void check(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') void check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const timer = window.setInterval(() => { void check(true); }, ACCOUNT_STATUS_POLL_MS);
    return () => {
      stopped = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(timer);
    };
  }, [userId, profileStatus]);

  const signUp = async (email: string, password: string, fullName: string, phoneNumber: string, businessName?: string) => {
    const redirectUrl = `${window.location.origin}/`;

    const referralCode = getCapturedReferral();
    const metaAttribution = getMetaAttribution();
    // Only carry truthy attribution keys into user_metadata (drop nulls).
    const metaMetadata = Object.fromEntries(
      Object.entries(metaAttribution).filter(([, v]) => Boolean(v))
    );
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          phone_number: phoneNumber,
          // The duplicate-trial hold matches a second signup from the same
          // device (handle_new_user_profile → signup_trial_hold).
          device_id: getDeviceId(),
          // Optional, from the signup form. handle_new_user_profile names the
          // organization after it (and fills organizations.business_name)
          // instead of the "<Name>'s Business" placeholder. Deliberately NOT
          // 'organization_name': that key routes to a second trigger which
          // skips referral credit, the signup notification and Meta
          // attribution.
          ...(businessName?.trim() ? { business_name: businessName.trim().slice(0, 120) } : {}),
          // Carried into handle_new_user_profile → apply_affiliate_referral.
          ...(referralCode ? { referral_code: referralCode } : {}),
          // Carried into handle_new_user_profile → org meta_fbc/meta_fbp.
          ...metaMetadata
        }
      }
    });

    if (error) {
      if (isExpectedAuthError(error)) {
        addSentryBreadcrumb('Sign up rejected', 'auth', { email, reason: error.message });
      } else {
        captureException(new Error('Sign up failed'), {
          authError: error,
          email: email,
          errorCode: error.message
        });
      }
      toast.error("Sign Up Error", {
        description: error.message,
      });
      return { error };
    }

    // Referral consumed (org created + attributed by the trigger) — don't reuse.
    if (referralCode) clearCapturedReferral();

    // Fire Meta CompleteRegistration + write the click id onto the CRM contact.
    // Best-effort: these must NEVER block or fail signup. Both require an
    // authenticated caller, so they only succeed when a session already exists
    // (immediate-login signup); the email-confirmation path derives auth later.
    const userId = data.user?.id;
    const hasAttribution = Object.values(metaAttribution).some(Boolean);
    if (userId && data.session) {
      const { meta_fbc, meta_fbp, fbclid, ...utmRest } = metaAttribution;
      const utm = {
        utm_source: utmRest.utm_source,
        utm_medium: utmRest.utm_medium,
        utm_campaign: utmRest.utm_campaign,
        utm_term: utmRest.utm_term,
        utm_content: utmRest.utm_content,
      };

      // Server-side CompleteRegistration (Meta CAPI), immune to ad-blockers.
      // Fire for EVERY signup — the hashed email is the match floor; fbc/fbp
      // just strengthen attribution when we actually captured an ad click.
      void supabase.functions
        .invoke('track-conversion', {
          body: {
            event: 'CompleteRegistration',
            event_id: `reg_${userId}`,
            fbc: meta_fbc ?? undefined,
            fbp: meta_fbp ?? undefined,
            utm,
          },
        })
        .then(({ error: fnError }) => {
          // Mark tracked so the deferred SIGNED_IN path never re-emits.
          if (!fnError) return supabase.auth.updateUser({ data: { meta_reg_tracked: true } });
        })
        .catch((e) => console.error('track-conversion (CompleteRegistration) failed', e));

      // CRM write-back only when we actually captured a click id / utm — stamp
      // it onto the user's marketing_contacts row (matched by email in the RPC).
      // Cast the client: set_contact_attribution isn't in the generated types yet.
      if (hasAttribution) {
        void (supabase as unknown as SetContactAttributionRpc)
          .rpc('set_contact_attribution', {
            p_fbclid: fbclid,
            p_fbc: meta_fbc,
            p_utm_source: utm.utm_source,
            p_utm_medium: utm.utm_medium,
            p_utm_campaign: utm.utm_campaign,
            p_utm_term: utm.utm_term,
            p_utm_content: utm.utm_content,
          })
          .then(({ error: rpcError }) => {
            if (rpcError) console.error('set_contact_attribution failed', rpcError);
          }, (e) => console.error('set_contact_attribution threw', e));

        clearMetaAttribution();
      }
    }

    // Check if email confirmation is needed
    const needsConfirmation = !data.session && data.user && !data.user.email_confirmed_at;

    if (needsConfirmation) {
      // Don't show toast, let the calling component handle redirect
      return { error: null, needsConfirmation: true };
    } else {
      toast("Welcome!", {
        description: "Your account has been created successfully.",
      });
      return { error: null, needsConfirmation: false };
    }
  };


  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      if (isExpectedAuthError(error)) {
        addSentryBreadcrumb('Sign in rejected', 'auth', { email, reason: error.message });
      } else {
        captureException(new Error('Sign in failed'), {
          authError: error,
          email: email,
          errorCode: error.message
        });
      }
      if (isAccountDeactivatedAuthError(error)) {
        toast.error(ACCOUNT_DEACTIVATED_TITLE, { description: ACCOUNT_DEACTIVATED_DESCRIPTION });
      } else {
        toast.error("Sign In Error", {
          description: error.message,
        });
      }
    } else {
      addSentryBreadcrumb('User signed in successfully', 'auth', { email });
      toast("Welcome back!", {
        description: "You have successfully signed in.",
      });
    }

    return { error };
  };

  const signInWithGoogle = async (redirectTo?: string | null) => {
    saveGoogleSignupRedirect(redirectTo);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/complete-profile`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });

    if (error) {
      captureException(new Error('Google sign in failed'), {
        authError: error,
        provider: 'google',
        errorCode: error.message
      });
      toast.error("Google Sign In Error", {
        description: error.message,
      });
    } else {
      addSentryBreadcrumb('Google OAuth initiated', 'auth', { provider: 'google' });
    }

    return { error };
  };

  const completeGoogleSignup = async (
    phoneNumber: string,
    businessName?: string,
    options?: { separateBusiness?: boolean },
  ) => {
    if (!user) return { error: new Error('Authentication required') };

    const wasIncomplete = profileStatus === 'signup_completion_required';
    const referralCode = getCapturedReferral();
    const attribution = getMetaAttribution();
    const { data: organizationId, error } = await (supabase as unknown as CompleteGoogleSignupRpc)
      .rpc('complete_google_signup', {
        p_phone: phoneNumber,
        p_business_name: businessName?.trim() || null,
        p_referral_code: referralCode,
        p_meta_fbc: attribution.meta_fbc,
        p_meta_fbp: attribution.meta_fbp,
        p_fbclid: attribution.fbclid,
        p_utm_source: attribution.utm_source,
        p_utm_medium: attribution.utm_medium,
        p_utm_campaign: attribution.utm_campaign,
        p_utm_term: attribution.utm_term,
        p_utm_content: attribution.utm_content,
        // The server holds a signup whose business domain already belongs to
        // an organization; this is the person saying "no, a separate company".
        p_separate_business: options?.separateBusiness === true,
        p_device_id: getDeviceId(),
      });

    if (error) return { error };

    // NULL back means no organization was founded: the signup is held as a
    // team join request for the organization already at that email domain.
    // Nothing to attribute or track yet; the page shows the pending state.
    if (organizationId === null) return { error: null, held: true };
    trackGoogleSignupCompleted({ userId: user.id, provider: user.app_metadata?.provider, wasIncomplete, organizationId });

    clearCapturedReferral();
    clearMetaAttribution();
    setProfileRefreshKey((key) => key + 1);

    // Refresh user metadata written by the RPC before firing registration.
    const { data } = await supabase.auth.getUser();
    if (data.user) await fireDeferredRegistrationTracking(data.user);

    return { error: null };
  };

  const signOut = async () => {
    resetAnalytics();
    // Before anything else: other tabs must not restore this user's session
    // from memory, even when the manual cleanup below is the only thing that
    // runs (no SIGNED_OUT broadcast, server session possibly still alive).
    markSignedOut(user?.id ?? session?.user?.id);
    // Sign-out must ALWAYS succeed locally. auth-js (2.57) returns the
    // session-load error (AuthSessionMissingError, a failed token refresh on
    // an expired session, …) BEFORE clearing local storage — so a dead or
    // corrupted session used to strand the user logged-in behind an
    // unremovable "Auth session missing" error. Signing out of a dead
    // session is success, not failure: on ANY error fall back to local-only
    // sign-out, then to manual cleanup, and never surface an error. Each
    // server-dependent attempt is time-boxed: with the server unreachable,
    // auth-js retries an expired session's refresh for ~30s inside signOut(),
    // and signing out while offline must not hang the UI for a minute.
    const attempt = async (call: () => Promise<{ error: unknown }>, ms: number) => {
      try {
        // .catch on the sign-out promise (not try/catch around the race) so a
        // rejection AFTER the timeout wins can't become an unhandled rejection.
        const guarded = call().catch((e): { error: unknown } => ({ error: e }));
        const { error } = await Promise.race([
          guarded,
          new Promise<{ error: unknown }>((resolve) =>
            setTimeout(() => resolve({ error: new Error('sign-out timed out') }), ms)),
        ]);
        return error;
      } catch (e) {
        return e as unknown;
      }
    };

    // scope:'local' — auth-js DEFAULTS to 'global', which revokes EVERY
    // session on EVERY device. That turned a phone sign-out into a "zombie
    // session" on the desktop (valid JWT, dead server session): REST kept
    // working but any auth.getUser() round-trip hit session_not_found →
    // auth-js _removeSession → random dumps to /auth mid-flow, and edge fns
    // 401'd "Invalid auth token" (2026-07-22 incident). Sign out signs out
    // THIS device only.
    let error = await attempt(() => supabase.auth.signOut({ scope: 'local' }), 5000);

    if (error) {
      // Telemetry only — the user still gets signed out below.
      captureException(new Error('Global sign out failed, falling back to local'), {
        authError: error,
        errorCode: (error as Error).message
      });
      error = await attempt(() => supabase.auth.signOut({ scope: 'local' }), 3000);
    }

    if (error) {
      // Local sign-out hits the same dead-session load path, so it can fail
      // too — drop the persisted session and app state directly. No
      // SIGNED_OUT event fires on this path, so clear state ourselves;
      // ProtectedRoute then redirects to /auth.
      addSentryBreadcrumb('Sign out fell back to manual local cleanup', 'auth');
      try {
        Object.keys(localStorage)
          .filter((k) => /^sb-.+-auth-token(-code-verifier)?$/.test(k))
          .forEach((k) => localStorage.removeItem(k));
      } catch { /* storage unavailable — the state reset still signs out the UI */ }
      setSession(null);
      setUser(null);
    }

    addSentryBreadcrumb('User signed out successfully', 'auth');
    // Clear Sentry user context (also happens in the auth state listener)
    clearSentryUser();
    return { error: null };
  };

  const value = {
    user,
    session,
    role,
    signUp,
    signIn,
    signInWithGoogle,
    completeGoogleSignup,
    refreshProfile: () => setProfileRefreshKey((key) => key + 1),
    signOut,
    loading,
    profileStatus,
    accessScope: resolvedAccess && resolvedAccess.userId === user?.id && profileStatus === 'ready' ? resolvedAccess.scope : null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
